import React, { useEffect, useRef } from 'react';
import { Box, Container, Heading } from '@qga/roo-ui/components';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';

import Tile from '@shared/propTypes/Tile';
import { DataLayerProvider } from '@shared/v2/contexts/DataLayerContext';

import FilterBanner from '@/components/CampaignPage/components/FilterBanner';
import ConditionsMarkdown from '@/components/ConditionsMarkdown';
import Destinations from '@/components/Destinations';
import DocumentTitle from '@/components/DocumentTitle';
import FAQs from '@/components/FAQs';
import GlobalCVPBanner from '@/components/GlobalCVPBanner';
import ImportantInformation from '@/components/ImportantInformation';
import Layout from '@/components/Layout';
import MetaDescription from '@/components/MetaDescription';
import Page from '@/components/Page';
import WebChat from '@/components/WebChat';
import Campaign from '@/propTypes/Campaign';
import Destination from '@/propTypes/Destination';
import DestinationLinks from '@/v2/components/ui/SiteFooter/components/DestinationLinks';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import usePageViewEvent from '@/v2/hooks/usePageViewEvent';
import useViewItemListEvent from '@/v2/hooks/useViewItemListEvent';
import CardListing from './components/CardListing';
import DataLayer from './components/DataLayer';
import HeroBanner from './components/HeroBanner';
import useCampaignData from './hooks/useCampaignData';
import useCampaignFilters from './hooks/useCampaignFilter';

const CampaignPage = ({ campaign, destinations, tiles }) => {
  const tilesScrollTo = useRef();
  const { campaignLandingPage: campaignPage } = campaign;
  const dataLayerData = { pageName: `${campaignPage.headline}` };
  const shouldUseNewEvents = useGA4Events();

  const {
    originCode,
    setOriginCode,
    originCodes,
    filteredProperties,
    destinationName,
    destinationNames,
    setDestinationName,
    filteredDestinations,
  } = useCampaignData(destinations, tiles);

  const { airportOriginCode, handleSetOrigin, handleSetDestination } =
    useCampaignFilters({
      setOriginCode,
      originCodes,
      setDestinationName,
      tilesScrollTo,
      shouldUseNewEvents,
    });

  usePageViewEvent({ type: 'campaign' });

  const { handleInvokeViewItemListEvent } = useViewItemListEvent({
    originCode: airportOriginCode,
    properties: tiles,
    title: campaignPage.headline,
    shouldAutoInvokeOnce: true,
  });

  useEffect(() => {
    handleInvokeViewItemListEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originCode]);

  return (
    <>
      <DataLayerProvider value={dataLayerData}>
        <Layout>
          <Page data-testid="CAMPAIGN_PAGE">
            <DataLayer campaign={campaign} />
            <DocumentTitle>{campaignPage.headline}</DocumentTitle>
            <MetaDescription content={campaignPage.tagline} />
            <HeroBanner campaign={campaign} />
            <Box py="4">
              <Container>
                <GlobalCVPBanner />
              </Container>
            </Box>
            <div ref={tilesScrollTo} />
            <FilterBanner
              originCode={originCode}
              originCodes={originCodes}
              setOriginCode={handleSetOrigin}
              destinationName={destinationName}
              destinationNames={destinationNames}
              destinations={destinations}
              setDestination={handleSetDestination}
            />
            <Box bg="greys.porcelain" id="featured-properties" pt={12}>
              <Container>
                {campaignPage.introCopy && campaignPage.introCopy.length && (
                  <Box>
                    <ReactMarkdown source={campaignPage.introCopy} />
                  </Box>
                )}
                <Box mx={[null, null, -4]} pt="6">
                  {campaignPage.tileHeadline &&
                    campaignPage.tileHeadline.length && (
                      <Heading.h3
                        fontWeight="normal"
                        mb={['4', null, '9']}
                        textAlign="center"
                      >
                        {campaignPage.tileHeadline}
                      </Heading.h3>
                    )}
                  <CardListing tiles={filteredProperties} />
                </Box>
                <Box py="10" fontSize="sm">
                  <ConditionsMarkdown
                    subtle
                    markdown={campaignPage.tileTermsConditions}
                  />
                </Box>
              </Container>
            </Box>
            {campaign.faqs && (
              <Box bg="greys.porcelain" pb="16">
                <FAQs faqs={campaign.faqs} />
              </Box>
            )}
            <Box py="12">
              <Container>
                <Destinations
                  heading={campaignPage.destinationHeadline}
                  destinations={filteredDestinations}
                />
              </Container>
            </Box>
          </Page>
          <Box bg="white">
            <Container>
              <Box pt="10">
                <ConditionsMarkdown
                  subtle
                  markdown={campaignPage.longFormTermsConditions}
                />
              </Box>
              <Box py="10">
                <ImportantInformation />
              </Box>
            </Container>
          </Box>
          <DestinationLinks />
        </Layout>
      </DataLayerProvider>
      <WebChat />
    </>
  );
};

CampaignPage.propTypes = {
  campaign: PropTypes.shape(Campaign).isRequired,
  destinations: PropTypes.arrayOf(PropTypes.shape(Destination)).isRequired,
  tiles: PropTypes.arrayOf(Tile).isRequired,
};

export default CampaignPage;
